import { faPenField, faTrashCan } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Box,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme
} from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import { FormikErrors, useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import React, { useContext, useEffect, useState } from "react"
import {
  UserCustomFieldDto,
  UserCustomFieldFormik,
  UserCustomFieldValueFieldFormik
} from "../edit/UserCustomField.interfaces"
import { CustomFieldCategory, UserCustomFieldSidebarProps } from "./userCustomField.interfaces"
import { userCustomFieldValidationSchema } from "./userCustomFieldValidationSchema"
import { StyledDivError } from "./userCustomFields.styled"
import { Sidebar } from "components/sidedrawer/Sidebar2";
import { StyledListItem } from "shared/styled/General"
import { StyledFormLabel } from "shared/styled/Global"
import { useTranslation } from "react-i18next"

const initialFormikValues: UserCustomFieldFormik = {
  id: 0,
  name: "",
  userCustomFieldCategoryId: 0,
  valueFields: [{ name: "", type: "", isRequired: false }],
  active: true
}

interface UserCustomFieldOption {
  name: string
  id: string
}

interface FormStuffProps {
  clickedUserCustomField?: UserCustomFieldDto
  categoriesWithInactive: CustomFieldCategory[]
}

const FormStuff = ({ clickedUserCustomField, categoriesWithInactive }: FormStuffProps) => {
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } = useFormikContext<UserCustomFieldFormik>()
  const theme = useTheme()
  const { t } = useTranslation()

  const everyFieldIsFilled = values.valueFields.every((valueField: UserCustomFieldValueFieldFormik) =>
    valueField.type !== "" && valueField.name !== ""
  )

  const userCustomFieldTypesOption: UserCustomFieldOption[] = [
    { name: t("global.number", "Number"), id: "Numeric" },
    { name: t("global.date", "Date"), id: "Date" },
    { name: t("global.textfield", "Text field"), id: "Text" },
    { name: t("global.yes_no", "Yes/No"), id: "Boolean" },
    { name: t("global.file.file", "File"), id: "File" }
  ]

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ height: "100%", gap: "1rem" }}>
      <StyledListItem>
        <StyledFormLabel>
          <Typography variant="h6" color="black">
            {t("global.name", "Name")}
          </Typography>
        </StyledFormLabel>
        <TextField
          placeholder={t("global.enter_name", "Enter name")}
          autoComplete="off"
          fullWidth
          type="text"
          id="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && errors.name ? true : false}
          helperText={touched.name && errors.name ? errors.name : null}
          variant="outlined"
        />

      </StyledListItem>
      <StyledListItem sx={{ gap: "8px" }}>
        {values.valueFields &&
          values.valueFields.length > 0 &&
          values.valueFields.map((_, index) => (
            <>
              <Typography variant="h6" color="black">
                {t("global.field", "Field")} {values.valueFields.length > 1 ? index + 1 : ""}
              </Typography>

              <TextField
                placeholder={t("global.enter_field_name", "Enter field name")}
                autoComplete="off"
                fullWidth
                type="text"
                id={`valueFields[${index}].name`}
                value={values.valueFields[index]?.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  Array.isArray(touched.valueFields) &&
                    touched.valueFields[index] &&
                    touched.valueFields[index].name &&
                    Array.isArray(errors.valueFields) &&
                    errors.valueFields[index] &&
                    (errors.valueFields[index] as FormikErrors<UserCustomFieldValueFieldFormik>)
                      .name
                    ? true
                    : false
                }
                helperText={
                  Array.isArray(touched.valueFields) &&
                    touched.valueFields[index] &&
                    touched.valueFields[index].name &&
                    Array.isArray(errors.valueFields) &&
                    errors.valueFields[index]
                    ? (
                      errors.valueFields[
                      index
                      ] as FormikErrors<UserCustomFieldValueFieldFormik>
                    ).name
                    : null
                }
                variant="outlined"
              />


              <ListItemText
                sx={{ width: "100%" }}
                secondary={
                  <Grid container alignItems="flex-start">
                    <Select
                      fullWidth
                      displayEmpty
                      disabled={values.valueFields[index]?.id !== undefined}
                      name={values.valueFields[index]?.type}
                      value={values.valueFields[index]?.type || ""}
                      onChange={(event) => {
                        setFieldValue(`valueFields[${index}].type`, event.target.value)
                      }}
                      onBlur={handleBlur}
                      error={
                        Array.isArray(touched.valueFields) &&
                          touched.valueFields[index] &&
                          touched.valueFields[index].type &&
                          Array.isArray(errors.valueFields) &&
                          errors.valueFields[index] &&
                          (errors.valueFields[index] as FormikErrors<UserCustomFieldValueFieldFormik>)
                            .type
                          ? true
                          : false
                      }
                      sx={{
                        color:
                          values.valueFields[index]?.type === ""
                            ? theme.palette.grey["500"]
                            : "inherit"
                      }}>
                      {values.valueFields[index]?.type === "" && (
                        <MenuItem value="" disabled>
                          {t("global.select_type", "Select type")}
                        </MenuItem>
                      )}
                      {userCustomFieldTypesOption.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {Array.isArray(touched.valueFields) &&
                      touched.valueFields[index] &&
                      touched.valueFields[index].type &&
                      Array.isArray(errors.valueFields) &&
                      errors.valueFields[index] &&
                      (errors.valueFields[index] as FormikErrors<UserCustomFieldValueFieldFormik>)
                        .type && (
                        <StyledDivError>
                          {
                            (
                              errors.valueFields[
                              index
                              ] as FormikErrors<UserCustomFieldValueFieldFormik>
                            ).type
                          }
                        </StyledDivError>
                      )}
                    <Grid container justifyContent="space-between" alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            sx={{ mt: 1 }}
                            checked={values.valueFields[index]?.isRequired}
                            onChange={(event) =>
                              setFieldValue(
                                `valueFields[${index}].isRequired`,
                                event.target.checked
                              )
                            }
                          />
                        }
                        label={t("global.show_as_required", "Show as required")}
                      />
                      {index > 0 && !clickedUserCustomField && (
                        <FontAwesomeIcon
                          size="lg"
                          icon={faTrashCan}
                          cursor="pointer"
                          onClick={() => {
                            const updatedValueFields = values.valueFields.filter(
                              (_, i) => i !== index
                            )
                            setFieldValue(`valueFields`, updatedValueFields)
                          }}
                        />
                      )}
                    </Grid>
                    {touched.valueFields && errors.valueFields && (
                      <StyledDivError>
                        {errors.valueFields[index] && typeof errors.valueFields[index] === "string"
                          ? errors.valueFields[index]
                          : null}
                      </StyledDivError>
                    )}
                  </Grid>
                }
              />
            </>
          ))}
      </StyledListItem>
      {everyFieldIsFilled && (
        <ListItem>
          <Typography
            onClick={() => {
              setFieldValue(`valueFields`, [
                ...values.valueFields,
                { name: "", type: "", isRequired: false }
              ])
            }}
            color="primary"
            sx={{ cursor: "pointer" }}>
            + {t("settings.user.create_one_more_field_type", "Create another field type")}
          </Typography>
        </ListItem>
      )}
      {errors.valueFields && typeof errors.valueFields === "string" && (
        <ListItem>
          <StyledDivError>{errors.valueFields}</StyledDivError>
        </ListItem>
      )}
      <StyledListItem>

        <StyledFormLabel>
          <Typography variant="h6" color="black">
            {t("global.category", "Category")}
          </Typography>
        </StyledFormLabel>

        <Select
          fullWidth
          name="userCustomFieldCategoryId"
          value={values.userCustomFieldCategoryId === 0 ? "" : values.userCustomFieldCategoryId}
          onChange={handleChange}
          onBlur={handleBlur}
          displayEmpty
          error={
            touched.userCustomFieldCategoryId && errors.userCustomFieldCategoryId
              ? true
              : false
          }
          sx={{
            color:
              values.userCustomFieldCategoryId === 0 || values.userCustomFieldCategoryId === -1
                ? theme.palette.grey["500"]
                : "inherit"
          }}
          renderValue={(selected: number | string) =>
            selected === "" || selected === 0 || selected === -1
              ? t("global.select_category", "Select category")
              : categoriesWithInactive.find((category) => category.id === selected)?.name
          }
        >
          {categoriesWithInactive.map((category: CustomFieldCategory) => (
            <MenuItem key={category.id} value={category.id} disabled={category.id === -1}>
              {category.name}
            </MenuItem>
          ))}
        </Select>
        {touched.userCustomFieldCategoryId && errors.userCustomFieldCategoryId && (
          <StyledDivError>{errors.userCustomFieldCategoryId}</StyledDivError>
        )}
      </StyledListItem>
      <Box
        pt={2}
        sx={{
          marginTop: "auto",
          bottom: 0,
          width: "100%",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: theme.palette.primary.contrastText
        }}>
        <StyledListItem>
          <FormControlLabel
            control={
              <Switch
                sx={{ mt: 1 }}
                onChange={(event) => {
                  setFieldValue("active", event.target.checked)
                }}
                checked={values.active}
              />
            }
            label={t("global.active", "Active")}
          />
        </StyledListItem>
      </Box>
    </Box>
  )
}

const formId = "UserCustomFieldForm";

export const UserCustomFieldSidebar = ({
  clickedUserCustomField,
  handleSubmit
}: UserCustomFieldSidebarProps) => {
  const appContext = useContext(AppContext)
  const [customerResult, customerApi] = useApi() as [IApiResult, IApiFunc]
  const [customFieldResult, customFieldApi] = useApi() as [IApiResult, IApiFunc]
  const [categoryResult, categoryApi] = useApi() as [IApiResult, IApiFunc]
  const [formikValues, setFormikValues] = useState<UserCustomFieldFormik>(initialFormikValues)
  const formRef = React.useRef<any>()
  const { t } = useTranslation()

  useEffect(() => {
    customerApi.get(apiEndpoints.customeradmin.getcustomer)
  }, [])

  useEffect(() => {
    if (!appContext.isSidebarOpen) return
    const url = apiEndpoints.usercustomfieldsadmin.getactivecustomfieldcategoriesbycustomerid
    categoryApi.get(url)
  }, [appContext.isSidebarOpen])

  useEffect(() => {
    if (customFieldResult.status !== 2) return
    appContext.toggleSideBar(formId, false)
    handleSubmit()
  }, [customFieldResult])

  useEffect(() => {
    if (appContext.isSidebarOpen) return
    setFormikValues(initialFormikValues)
    formRef?.current?.resetForm()
  }, [appContext.isSidebarOpen])

  useEffect(() => {
    if (!clickedUserCustomField) return
    const userCustomFieldCategoryId = clickedUserCustomField.category.active
      ? clickedUserCustomField.category.id
      : -1
    setFormikValues({
      id: clickedUserCustomField.id,
      name: clickedUserCustomField.name,
      active: clickedUserCustomField.active,
      valueFields: clickedUserCustomField.valueFields.map((valueField) => ({
        id: valueField.id,
        name: valueField.name,
        type: valueField.type,
        isRequired: valueField.isRequired
      })),
      userCustomFieldCategoryId: userCustomFieldCategoryId
    })
  }, [clickedUserCustomField])

  const handleFormikSubmit = (formikValues: UserCustomFieldFormik) => {
    const userCustomFieldData: Omit<UserCustomFieldFormik, "id"> = {
      name: formikValues.name,
      valueFields: formikValues.valueFields,
      active: formikValues.active,
      userCustomFieldCategoryId: formikValues.userCustomFieldCategoryId
    }
    if (clickedUserCustomField) {
      const url = apiEndpoints.usercustomfieldsadmin.updateusercustomfield
      customFieldApi.put(url, { id: clickedUserCustomField.id, ...userCustomFieldData })
    } else {
      const url = apiEndpoints.usercustomfieldsadmin.createusercustomfield
      customFieldApi.post(url, userCustomFieldData)
    }
  }

  const activeCategories: CustomFieldCategory[] =
    categoryResult.status === 2 && categoryResult.data ? categoryResult.data : []
  const categoriesWithInactive: CustomFieldCategory[] =
    clickedUserCustomField &&
      !activeCategories.find(
        (category: CustomFieldCategory) => category.id === clickedUserCustomField.category.id
      )
      ? [
        ...activeCategories,
        {
          id: -1,
          name: `${clickedUserCustomField.category.name} (${t("global.inactive", "Inactive")})`,
          customerId: customerResult.data.id,
          active: false
        }
      ]
      : activeCategories

  return (
    <Sidebar
      initialFormikValues={formikValues}
      setInitialFormikValues={setFormikValues}
      validationSchema={userCustomFieldValidationSchema(t)}
      formResult={customFieldResult}
      handleSubmit={handleFormikSubmit}
      formData={{
        formId: formId,
        titleNew: t("settings.user.create_custom_field", "Create custom field"),
        subTitleNew: t("settings.user.create_a_new_custom_field", "Create a new custom field"),
        titleEdit: t("global.edit_field", "Edit field"),
        subTitleEdit: t("settings.user.edit_a_custom_field", "Edit a custom field"),
        submitText: t("global.save", "Save"),
        submitEditText: t("global.save", "Save"),
        icon: faPenField,
      }}
      formRef={formRef}
    >
      <FormStuff
        clickedUserCustomField={clickedUserCustomField}
        categoriesWithInactive={categoriesWithInactive}
      />
    </Sidebar>
  )
}
